import { CaretDownIcon } from "@phosphor-icons/react/dist/csr/CaretDown";
import { XIcon } from "@phosphor-icons/react/dist/csr/X";
import { type ButtonHTMLAttributes, forwardRef, type ReactNode } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { cn } from "@volca/ui/lib/utils";

export interface FilterChipProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  value?: string;
  placeholder?: string;
  onClear?: () => void;
  active?: boolean;
  icon?: ReactNode;
  size?: "default" | "sm";
}

export const FilterChip = forwardRef<HTMLButtonElement, FilterChipProps>(
  (
    {
      label,
      value,
      placeholder = "All values",
      onClear,
      active,
      icon,
      size = "default",
      className,
      disabled,
      ...props
    },
    ref,
  ) => {
    const selected = active ?? value !== undefined;
    const hasClear = selected && !!onClear;
    return (
      <Stack
        direction="row"
        align="center"
        gap={0}
        className={cn(
          "w-fit max-w-full min-w-0 shrink-0 rounded-full border text-sm transition-colors",
          size === "sm" ? "h-7" : "h-9 sm:h-8",
          "has-[[data-slot=filter-chip-trigger]:focus-visible]:border-focus has-[[data-slot=filter-chip-trigger]:focus-visible]:ring-3 has-[[data-slot=filter-chip-trigger]:focus-visible]:ring-focus/50",
          selected
            ? "border-selected-line bg-selected"
            : "border-line bg-surface-raised hover:bg-neutral-hovered",
          { "opacity-50": disabled },
        )}
      >
        <button
          ref={ref}
          type="button"
          aria-label={`${label}: ${value ?? placeholder}`}
          title={value}
          {...props}
          data-slot="filter-chip-trigger"
          disabled={disabled}
          className={cn(
            "flex h-full min-w-0 items-center gap-1.5 rounded-full pr-2.5 pl-3 outline-none disabled:pointer-events-none",
            { "pr-1": hasClear },
            className,
          )}
        >
          <span className="shrink-0 text-subtle">{label}</span>
          {value !== undefined && (
            <span
              className="max-w-52 truncate font-medium text-ink"
              title={value}
            >
              {value}
            </span>
          )}
          {icon === undefined
            ? value === undefined && (
                <CaretDownIcon
                  aria-hidden
                  className="size-4 shrink-0 text-subtle"
                />
              )
            : icon}
        </button>
        {hasClear && (
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label={`Clear ${label} filter`}
            disabled={disabled}
            onClick={onClear}
            className={cn(
              "shrink-0 rounded-full text-subtle",
              size === "sm" ? "mr-0.5" : "mr-1",
            )}
          >
            <XIcon aria-hidden />
          </Button>
        )}
      </Stack>
    );
  },
);

FilterChip.displayName = "FilterChip";
